// useWeather — browser geolocation + Open-Meteo current conditions.
//
// Why client-direct (no backend RPC):
//   • Open-Meteo has CORS open for public use — no proxy needed.
//   • No API key, no rate-limit concerns at household scale.
//   • Server changes here would require a docker rebuild/redeploy
//     loop; this hook ships purely via the Vite bundle.
//
// Strategy:
//   1. Read cached coords + last response from sessionStorage so
//      navigate-back doesn't re-prompt or re-fetch.
//   2. If no cached coords, call navigator.geolocation.getCurrentPosition.
//      Permission is sticky in modern browsers; once granted, future
//      page loads resolve immediately without UI.
//   3. Fetch current temp + today's high/low from Open-Meteo.
//   4. Cache the response for ~30 minutes; refresh on next mount after
//      that.

import { useEffect, useState } from "preact/hooks";

export interface WeatherSnapshot {
  temp: number;
  high: number;
  low: number;
  label: string;
  /** Local timestamp the snapshot was fetched. Used for cache TTL. */
  fetchedAt: number;
}

export interface UseWeather {
  weather: WeatherSnapshot | null;
  loading: boolean;
  /** Set when the user denied geolocation or the API call failed.
   *  The Today hero hides the card on error rather than nagging. */
  error: string | null;
}

const COORDS_KEY = "hub.weather.coords";
const SNAPSHOT_KEY = "hub.weather.snapshot";
const CACHE_TTL_MS = 30 * 60 * 1000;

interface CachedCoords {
  lat: number;
  lon: number;
  at: number;
}

// WMO codes → short labels. Anything outside this table renders as a
// generic "Unsettled" so we don't leave the card showing a raw number.
const WEATHER_LABELS: Record<number, string> = {
  0: "Clear",
  1: "Mostly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Foggy",
  48: "Foggy",
  51: "Drizzle",
  53: "Drizzle",
  55: "Heavy drizzle",
  56: "Freezing drizzle",
  57: "Freezing drizzle",
  61: "Light rain",
  63: "Rain",
  65: "Heavy rain",
  66: "Freezing rain",
  67: "Freezing rain",
  71: "Light snow",
  73: "Snow",
  75: "Heavy snow",
  77: "Snow grains",
  80: "Showers",
  81: "Showers",
  82: "Heavy showers",
  85: "Snow showers",
  86: "Snow showers",
  95: "Thunderstorms",
  96: "Thunderstorms",
  99: "Thunderstorms",
};

export function useWeather(): UseWeather {
  const [weather, setWeather] = useState<WeatherSnapshot | null>(() => {
    // Hydrate from sessionStorage so a Today tab re-mount doesn't
    // flash empty before the network round-trip lands.
    try {
      const raw = sessionStorage.getItem(SNAPSHOT_KEY);
      if (!raw) return null;
      const parsed: WeatherSnapshot = JSON.parse(raw);
      if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
      return parsed;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Already have a fresh snapshot — nothing to do until the cache
    // expires (next mount after 30 min picks up the empty state).
    if (weather) return;

    let cancelled = false;
    setLoading(true);

    const fetchFor = async (lat: number, lon: number) => {
      // Open-Meteo current weather + today's daily extremes. timezone=auto
      // resolves the daily window to the user's local day, so "high/low"
      // matches what they'd see on a weather app.
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}`
        + `&longitude=${lon}`
        + `&current=temperature_2m,weather_code`
        + `&daily=temperature_2m_max,temperature_2m_min`
        + `&temperature_unit=fahrenheit`
        + `&timezone=auto`
        + `&forecast_days=1`;
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`weather ${res.status}`);
        const j = await res.json();
        const temp = Math.round(j.current?.temperature_2m ?? 0);
        const code = Number(j.current?.weather_code ?? 0);
        const high = Math.round(j.daily?.temperature_2m_max?.[0] ?? temp);
        const low  = Math.round(j.daily?.temperature_2m_min?.[0] ?? temp);
        const snap: WeatherSnapshot = {
          temp, high, low,
          label: WEATHER_LABELS[code] || "Unsettled",
          fetchedAt: Date.now(),
        };
        if (cancelled) return;
        setWeather(snap);
        try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap)); } catch {}
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "weather failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Try cached coords first — once the user has granted permission
    // once, we reuse the location for the whole session so we don't
    // even hit the geolocation API a second time.
    let cachedCoords: CachedCoords | null = null;
    try {
      const raw = sessionStorage.getItem(COORDS_KEY);
      if (raw) cachedCoords = JSON.parse(raw);
    } catch {}

    if (cachedCoords && Number.isFinite(cachedCoords.lat) && Number.isFinite(cachedCoords.lon)) {
      fetchFor(cachedCoords.lat, cachedCoords.lon);
      return () => { cancelled = true; };
    }

    if (!navigator.geolocation) {
      setError("geolocation not supported");
      setLoading(false);
      return () => { cancelled = true; };
    }

    // Browser permission prompt. enableHighAccuracy:false because we
    // only need city-level resolution for the weather call — the GPS
    // ladder is unnecessary overhead.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const coords: CachedCoords = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          at: Date.now(),
        };
        try { sessionStorage.setItem(COORDS_KEY, JSON.stringify(coords)); } catch {}
        fetchFor(coords.lat, coords.lon);
      },
      (err) => {
        if (cancelled) return;
        // PERMISSION_DENIED (1), POSITION_UNAVAILABLE (2), TIMEOUT (3)
        setError(err.code === 1 ? "permission denied" : err.message || "geolocation failed");
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 },
    );

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { weather, loading, error };
}
